import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { MapPin, Loader2, Check, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

/**
 * Address autocomplete via Google Places (New) con fallback OSM.
 * Il backend decide la provider in base alla presenza di GOOGLE_MAPS_API_KEY.
 * value: {display, lat, lon, place_id, city, postcode, country, ...}
 * onChange payload identico al vecchio componente, con l'aggiunta di place_id.
 */
function newSessionToken() {
  // UUID v4 lightweight per Google Places sessionToken (ottimizzazione billing)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function AddressAutocomplete({ label, value, onChange, testid, placeholder = "Cerca indirizzo o luogo...", className = "" }) {
  const [q, setQ] = useState(value?.display || value?.text || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [provider, setProvider] = useState(null);
  const [notSelected, setNotSelected] = useState(false);
  const sessionTokenRef = useRef(newSessionToken());
  const wrap = useRef(null);

  useEffect(() => {
    setQ(value?.display || value?.text || "");
  }, [value?.display, value?.text]);

  useEffect(() => {
    if (!q || q.length < 3) { setResults([]); return; }
    // Skip autocomplete solo se il valore corrente ha già le coordinate (= già selezionato prima).
    if (q === (value?.display || "") && value?.lat != null) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/geocode/search", { params: { q, limit: 6, session_token: sessionTokenRef.current } });
        setResults(r.data?.results || []);
        setProvider(r.data?.provider || null);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = async (r) => {
    setOpen(false);
    setNotSelected(false);
    // Google: risolvo lat/lon tramite /geocode/place-details
    if (r.source === "google" && r.place_id && (r.lat == null || r.lon == null)) {
      setLoading(true);
      try {
        const d = await api.get("/geocode/place-details", { params: { place_id: r.place_id, session_token: sessionTokenRef.current } });
        if (d.data?.lat != null) {
          const full = { ...r, ...d.data, text: d.data.display };
          setQ(full.display);
          onChange && onChange(full);
          // nuovo session token dopo ogni selezione (billing optimization Google)
          sessionTokenRef.current = newSessionToken();
          setLoading(false);
          return;
        }
      } catch (_e) { /* fallback sotto */ }
      setLoading(false);
    }
    // OSM (fallback) o Google con lat/lon già presenti
    setQ(r.display);
    onChange && onChange({ ...r, text: r.display });
    sessionTokenRef.current = newSessionToken();
  };

  const onManual = (v) => {
    setQ(v);
    setNotSelected(!!v && v !== (value?.display || ""));
    // Preservo l'indirizzo testuale ma invalido coordinate/place_id del vecchio valore
    onChange && onChange({ display: v, text: v, lat: null, lon: null, place_id: null });
  };

  const hasCoords = value?.lat != null && value?.lon != null;

  return (
    <div ref={wrap} className={`relative ${className}`}>
      {label && <div className="text-xs text-slate-500 mb-1">{label}</div>}
      <div className="relative">
        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        <Input
          data-testid={testid}
          value={q}
          placeholder={placeholder}
          onChange={(e) => onManual(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="pl-9 bg-slate-900 border-slate-800"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 animate-spin" />}
        {hasCoords && !loading && <Check size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400" />}
      </div>
      {open && results.length > 0 && (
        <div
          data-testid={`${testid}-results`}
          className="absolute top-full mt-1 left-0 right-0 bg-slate-950 border border-slate-800 rounded-xl shadow-2xl max-h-72 overflow-y-auto z-[70]"
        >
          {results.map((r, i) => (
            <button
              type="button"
              key={r.place_id || i}
              data-testid={`${testid}-result-${i}`}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-2.5 hover:bg-slate-900 border-b border-slate-800/50 last:border-0"
            >
              <div className="text-sm text-white truncate flex items-center gap-2">
                <MapPin size={12} className="text-amber-400 flex-shrink-0" />
                <span className="truncate">{r.name || r.display}</span>
              </div>
              <div className="text-xs text-slate-500 truncate pl-5">{r.secondary || r.display}</div>
            </button>
          ))}
          {provider && (
            <div className="px-3 py-1.5 text-[10px] text-slate-600 bg-slate-950 border-t border-slate-800/50 text-right">
              Suggerimenti: {provider === "google" ? "Google Places" : "OpenStreetMap"}
            </div>
          )}
        </div>
      )}
      {notSelected && !hasCoords && q.length >= 3 && !open && (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-amber-500/80">
          <AlertTriangle size={10} /> Seleziona un indirizzo dai risultati per salvare le coordinate.
        </div>
      )}
    </div>
  );
}
